import { useEffect, useMemo, useState } from "react";
import { ClipboardCheck, Contact, MapPinned, PencilRuler, ShieldAlert } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components/Button";
import { Card } from "../../../components/Card";
import { Chip } from "../../../components/Chip";
import { Checkbox } from "../../../components/Checkbox";
import { FormField } from "../../../components/FormField";
import { Notice } from "../../../components/Notice";
import { PageHeader } from "../../../components/PageHeader";
import { ProgressMeter } from "../../../components/ProgressMeter";
import { ReadinessDial } from "../../../components/ReadinessDial";
import { StepRail } from "../../../components/StepRail";
import { StatusBadge } from "../../../components/StatusBadge";
import { TurnstileField } from "../../../components/TurnstileField";
import { buildRuleBasedScope } from "../../../lib/ai/ruleScopeAssistant";
import { cloudBoardEnabled, publishJobToCloud } from "../../../lib/cloudApi";
import { db } from "../../../lib/db";
import { makeId } from "../../../lib/id";
import { calculateReadiness } from "../../../lib/readiness";
import { analyzeRisk, hasLicensedTradeRisk } from "../../../lib/risk";
import { formatZodError, jobPostSchema } from "../../../lib/schemas";
import type {
  ContactMethod,
  DraftJob,
  JobCategory,
  JobPost,
  JobScopeItem,
  JobUrgency,
  ScopeCategory,
} from "../../../types";

const activeDraftId = "active-post-draft";

const categoryOptions: JobCategory[] = [
  "ASSEMBLY",
  "BASIC_LABOR",
  "DRYWALL_PAINT",
  "ELECTRICAL",
  "HANDYMAN",
  "LANDSCAPE",
  "MOVING",
  "PLUMBING",
  "PROPERTY_MAINTENANCE",
];

const urgencyOptions: JobUrgency[] = ["ROUTINE", "SOON", "URGENT", "EMERGENCY"];
const contactOptions: ContactMethod[] = ["EMAIL", "SMS", "PHONE"];
const scopeCategories: ScopeCategory[] = ["SIZE", "ACTIVITY", "LOCATION", "TIME", "EQUIPMENT"];
const steps = [
  { id: "scope", name: "Job", description: "Plain-language job details", icon: PencilRuler },
  { id: "risk", name: "Safety", description: "Completeness and trade warnings", icon: ShieldAlert },
  {
    id: "location",
    name: "Where and when",
    description: "Timing, budget, and materials",
    icon: MapPinned,
  },
  { id: "contact", name: "Send to a pro", description: "Contact preferences", icon: Contact },
  { id: "review", name: "Done", description: "Review and save", icon: ClipboardCheck },
];

type JobForm = DraftJob["payload"]["form"];

function blankScope(): JobScopeItem[] {
  return scopeCategories.map((category) => ({
    id: makeId("scope"),
    category,
    prompt: category,
    description: "",
    isVerified: false,
  }));
}

function blankForm(): JobForm {
  return {
    title: "",
    summary: "",
    category: "HANDYMAN",
    neighborhood: "",
    city: "",
    state: "ME",
    budgetMin: "",
    budgetMax: "",
    desiredWindow: "",
    urgency: "ROUTINE",
    primaryContact: "",
    alternateContact: "",
    contingencyContact: "",
    preferredMethod: "EMAIL",
    materialNotes: "",
    highRiskAcknowledged: false,
  };
}

function buildDraft(
  form: JobForm,
  scopeItems: JobScopeItem[],
  roughNotes: string,
  currentStep: number,
): DraftJob {
  return {
    id: activeDraftId,
    updatedAt: new Date().toISOString(),
    schemaVersion: 2,
    payload: {
      roughNotes,
      form,
      scopeItems,
      currentStep,
    },
  };
}

function formatLocalWriteError(cause: unknown): string {
  if (cause instanceof DOMException && cause.name === "QuotaExceededError") {
    return "Your device storage is full. Export or remove older records before saving more work.";
  }
  if (cause instanceof Error) {
    return cause.message;
  }
  return "Local storage write failed.";
}

export function PostJobPage() {
  const navigate = useNavigate();
  const [roughNotes, setRoughNotes] = useState("");
  const [scopeItems, setScopeItems] = useState<JobScopeItem[]>(blankScope);
  const [form, setForm] = useState<JobForm>(blankForm);
  const [currentStep, setCurrentStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState("Draft not saved yet.");
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [publishToCloud, setPublishToCloud] = useState(cloudBoardEnabled);
  const [turnstileToken, setTurnstileToken] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let live = true;
    void db.drafts.get(activeDraftId).then((draft) => {
      if (!live) {
        return;
      }
      if (draft) {
        setRoughNotes(draft.payload.roughNotes);
        setScopeItems(draft.payload.scopeItems);
        setForm(draft.payload.form);
        setCurrentStep(draft.payload.currentStep);
        setSaveStatus(`Draft restored from ${new Date(draft.updatedAt).toLocaleString()}.`);
      }
      setDraftLoaded(true);
    });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    if (!draftLoaded) {
      return;
    }
    const handle = window.setTimeout(() => {
      void db.drafts
        .put(buildDraft(form, scopeItems, roughNotes, currentStep))
        .then(() => {
          setSaveStatus("Draft saved locally.");
        })
        .catch((cause: unknown) => {
          setError(formatLocalWriteError(cause));
          setSaveStatus("Draft save blocked.");
        });
    }, 400);
    return () => window.clearTimeout(handle);
  }, [currentStep, draftLoaded, form, roughNotes, scopeItems]);

  const riskFlags = useMemo(() => {
    const text = [
      form.title,
      form.summary,
      roughNotes,
      scopeItems.map((item) => item.description).join(" "),
    ].join(" ");
    return analyzeRisk(text, form.category);
  }, [form.category, form.summary, form.title, roughNotes, scopeItems]);

  const readiness = useMemo(
    () => calculateReadiness(scopeItems, riskFlags),
    [scopeItems, riskFlags],
  );
  const licensedRisk = hasLicensedTradeRisk(riskFlags);
  const verifiedCount = scopeItems.filter((item) => item.isVerified).length;

  function updateForm(patch: Partial<JobForm>) {
    setForm((current) => ({ ...current, ...patch }));
  }

  function updateScope(category: ScopeCategory, description: string) {
    setScopeItems((current) =>
      current.map((item) =>
        item.category === category
          ? { ...item, description, isVerified: description.trim().length >= 8 }
          : item,
      ),
    );
  }

  function generateScope() {
    const next = buildRuleBasedScope(roughNotes || form.summary || form.title);
    setScopeItems(next);
    setCurrentStep(1);
  }

  async function submit() {
    setError(null);
    setIsSubmitting(true);
    if (licensedRisk && !form.highRiskAcknowledged) {
      setError("Acknowledge the safety warning before saving this job.");
      setCurrentStep(1);
      setIsSubmitting(false);
      return;
    }

    const now = new Date().toISOString();
    const job: JobPost = {
      id: makeId("job"),
      createdAt: now,
      updatedAt: now,
      title: form.title.trim(),
      summary: form.summary.trim(),
      category: form.category,
      neighborhood: form.neighborhood.trim(),
      city: form.city.trim(),
      state: form.state.trim(),
      budgetMin: form.budgetMin ? Number(form.budgetMin) : undefined,
      budgetMax: form.budgetMax ? Number(form.budgetMax) : undefined,
      desiredWindow: form.desiredWindow.trim(),
      urgency: form.urgency,
      scopeItems,
      riskFlags,
      readiness,
      contact: {
        primary: form.primaryContact.trim(),
        alternate: form.alternateContact.trim() || undefined,
        contingency: form.contingencyContact.trim() || undefined,
        preferredMethod: form.preferredMethod,
      },
      status: "OPEN",
      materialNotes: form.materialNotes.trim() || undefined,
    };

    const result = jobPostSchema.safeParse(job);
    if (!result.success) {
      setError(formatZodError(result.error));
      setIsSubmitting(false);
      return;
    }

    try {
      await db.transaction("rw", [db.jobs, db.drafts], async () => {
        await db.jobs.put(result.data);
        if (!publishToCloud) {
          await db.drafts.delete(activeDraftId);
        }
      });

      if (publishToCloud) {
        await publishJobToCloud(result.data, turnstileToken);
        await db.drafts.delete(activeDraftId);
      }

      navigate(`/jobs/${result.data.id}`);
    } catch (cause) {
      const detail = cause instanceof Error ? cause.message : formatLocalWriteError(cause);
      setError(
        publishToCloud
          ? `Local save is complete, but the public post was blocked: ${detail}`
          : detail,
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="stack-section">
      <PageHeader
        action={
          <Chip size="md">
            Step {currentStep + 1} of 5 - Detail {readiness.score}/100
          </Chip>
        }
        eyebrow="Post a job"
        title="Tell us about your job"
      >
        In about 2 minutes, you will have a clean job brief you can send to any local pro.
      </PageHeader>

      <Notice title="Heads up" tone="warning">
        ClearScope is a free tool to organize your job. We do not vet or recommend contractors, so
        always check credentials before hiring.
      </Notice>

      {error ? (
        <Notice title="Submission blocked" tone="danger">
          {error}
        </Notice>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[19rem_minmax(0,1fr)] xl:grid-cols-[19rem_minmax(0,1fr)_22rem] xl:items-start">
        <StepRail
          currentStep={currentStep}
          status={{
            label: saveStatus,
            tone: saveStatus.includes("blocked") ? "danger" : "success",
          }}
          steps={steps}
          onStep={setCurrentStep}
        />
        <section className="space-y-5">
          <div className="card-hairline flex flex-wrap items-center justify-between gap-3 p-4 xl:hidden">
            <div>
              <p className="page-eyebrow">Detail score</p>
              <p className="mt-1 text-sm font-semibold text-text-muted">{readiness.guidance}</p>
            </div>
            <StatusBadge
              dotPosition="trailing"
              level={readiness.level}
              label={`${readiness.level} ${readiness.score}/100`}
            />
          </div>
          {currentStep === 0 ? (
            <Card className="grid gap-4" padding="lg" variant="surface">
              <div>
                <p className="page-eyebrow">01 - Job basics</p>
                <h2 className="section-title mt-2 font-semibold text-text">What needs done?</h2>
              </div>
              <FormField
                id="title"
                label="Job title"
                size="lg"
                valid={form.title.trim().length >= 4}
                value={form.title}
                onChange={(event) => updateForm({ title: event.currentTarget.value })}
              />
              <FormField
                id="summary"
                label="What needs done?"
                kind="textarea"
                maxChars={320}
                rows={4}
                valid={form.summary.trim().length >= 20}
                value={form.summary}
                onChange={(event) => updateForm({ summary: event.currentTarget.value })}
              />
              <FormField
                id="roughNotes"
                label="Add any extra details (optional)"
                hint="We will help fill in the details. Nothing is sent anywhere."
                kind="textarea"
                maxChars={520}
                rows={5}
                value={roughNotes}
                onChange={(event) => setRoughNotes(event.currentTarget.value)}
              />
              <div className="flex flex-wrap gap-3">
                <Button type="button" variant="tonal" onClick={generateScope}>
                  Fill in details for me
                </Button>
                <Button type="button" onClick={() => setCurrentStep(1)}>
                  Safety
                </Button>
              </div>
            </Card>
          ) : null}

          {currentStep === 1 ? (
            <Card className="grid gap-4" padding="lg" variant="surface">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="page-eyebrow">02 - Safety and details</p>
                  <h2 className="section-title mt-2 font-semibold text-text">Review the details</h2>
                  <p className="mt-2 text-sm leading-6 text-text-muted">
                    Confirm the five job details and acknowledge licensed work warnings when needed.
                  </p>
                </div>
                <StatusBadge
                  level={readiness.level}
                  label={`${readiness.level} ${readiness.score}/100`}
                  dotPosition="trailing"
                />
              </div>
              <div className="grid gap-4">
                {scopeItems.map((item) => (
                  <FormField
                    key={item.category}
                    id={`scope-${item.category}`}
                    label={item.category}
                    kind="textarea"
                    rows={3}
                    valid={item.isVerified}
                    value={item.description}
                    onChange={(event) => updateScope(item.category, event.currentTarget.value)}
                  />
                ))}
              </div>
              <div className="grid gap-3 border-l-4 border-danger py-1 pl-4">
                {licensedRisk ? (
                  <Notice compact title="May need a licensed pro" tone="danger">
                    This job may require licensing, permits, insurance, or qualified review.
                    ClearScope does not verify legal compliance.
                  </Notice>
                ) : null}
                <Checkbox
                  checked={form.highRiskAcknowledged}
                  label="I understand ClearScope does not verify contractors and I will check credentials myself."
                  onChange={(event) =>
                    updateForm({ highRiskAcknowledged: event.currentTarget.checked })
                  }
                />
              </div>
              <div className="flex flex-wrap gap-3">
                <Button type="button" variant="secondary" onClick={() => setCurrentStep(0)}>
                  Back
                </Button>
                <Button type="button" onClick={() => setCurrentStep(2)}>
                  Where and when
                </Button>
              </div>
            </Card>
          ) : null}

          {currentStep === 2 ? (
            <Card className="grid gap-4" padding="lg" variant="surface">
              <div>
                <p className="page-eyebrow">03 - Where and when</p>
                <h2 className="section-title mt-2 font-semibold text-text">
                  Set the visit details
                </h2>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  id="category"
                  label="Category"
                  kind="select"
                  value={form.category}
                  onChange={(event) =>
                    updateForm({ category: event.currentTarget.value as JobCategory })
                  }
                >
                  {categoryOptions.map((category) => (
                    <option key={category} value={category}>
                      {category.replace(/_/g, " ")}
                    </option>
                  ))}
                </FormField>
                <FormField
                  id="urgency"
                  label="Urgency"
                  kind="select"
                  value={form.urgency}
                  onChange={(event) =>
                    updateForm({ urgency: event.currentTarget.value as JobUrgency })
                  }
                >
                  {urgencyOptions.map((urgency) => (
                    <option key={urgency} value={urgency}>
                      {urgency}
                    </option>
                  ))}
                </FormField>
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                <FormField
                  id="neighborhood"
                  label="Neighborhood"
                  valid={form.neighborhood.trim().length >= 2}
                  value={form.neighborhood}
                  onChange={(event) => updateForm({ neighborhood: event.currentTarget.value })}
                />
                <FormField
                  id="city"
                  label="City"
                  valid={form.city.trim().length >= 2}
                  value={form.city}
                  onChange={(event) => updateForm({ city: event.currentTarget.value })}
                />
                <FormField
                  id="state"
                  label="State"
                  valid={form.state.trim().length >= 2}
                  value={form.state}
                  onChange={(event) => updateForm({ state: event.currentTarget.value })}
                />
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                <FormField
                  id="budgetMin"
                  label="Budget minimum"
                  type="number"
                  min="0"
                  value={form.budgetMin}
                  onChange={(event) => updateForm({ budgetMin: event.currentTarget.value })}
                />
                <FormField
                  id="budgetMax"
                  label="Budget maximum"
                  type="number"
                  min="0"
                  value={form.budgetMax}
                  onChange={(event) => updateForm({ budgetMax: event.currentTarget.value })}
                />
                <FormField
                  id="desiredWindow"
                  label="Desired window"
                  value={form.desiredWindow}
                  onChange={(event) => updateForm({ desiredWindow: event.currentTarget.value })}
                />
              </div>
              <FormField
                id="materialNotes"
                label="Material notes"
                kind="textarea"
                rows={3}
                value={form.materialNotes}
                onChange={(event) => updateForm({ materialNotes: event.currentTarget.value })}
              />
              <div className="flex flex-wrap gap-3">
                <Button type="button" variant="secondary" onClick={() => setCurrentStep(1)}>
                  Back
                </Button>
                <Button type="button" onClick={() => setCurrentStep(3)}>
                  Contact
                </Button>
              </div>
            </Card>
          ) : null}

          {currentStep === 3 ? (
            <Card className="grid gap-4" padding="lg" variant="surface">
              <div>
                <p className="page-eyebrow">04 - Contact preferences</p>
                <h2 className="section-title mt-2 font-semibold text-text">
                  Choose contact options
                </h2>
              </div>
              <FormField
                id="primaryContact"
                label="Primary contact"
                valid={form.primaryContact.trim().length >= 5}
                value={form.primaryContact}
                onChange={(event) => updateForm({ primaryContact: event.currentTarget.value })}
              />
              <FormField
                id="alternateContact"
                label="Alternate contact"
                value={form.alternateContact}
                onChange={(event) => updateForm({ alternateContact: event.currentTarget.value })}
              />
              <FormField
                id="contingencyContact"
                label="Contingency note"
                value={form.contingencyContact}
                onChange={(event) => updateForm({ contingencyContact: event.currentTarget.value })}
              />
              <FormField
                id="preferredMethod"
                label="Preferred method"
                kind="select"
                value={form.preferredMethod}
                onChange={(event) =>
                  updateForm({ preferredMethod: event.currentTarget.value as ContactMethod })
                }
              >
                {contactOptions.map((method) => (
                  <option key={method} value={method}>
                    {method}
                  </option>
                ))}
              </FormField>
              <div className="flex flex-wrap gap-3">
                <Button type="button" variant="secondary" onClick={() => setCurrentStep(2)}>
                  Back
                </Button>
                <Button type="button" onClick={() => setCurrentStep(4)}>
                  Done
                </Button>
              </div>
            </Card>
          ) : null}

          {currentStep === 4 ? (
            <Card className="grid gap-5" padding="lg" variant="surface">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="page-eyebrow">05 - Done</p>
                  <h2 className="section-title mt-2 font-semibold text-text">Ready to post</h2>
                  <p className="mt-2 text-sm leading-6 text-text-muted">
                    Saving keeps a copy in this browser. You can also post it to the public board.
                  </p>
                </div>
                <StatusBadge
                  level={readiness.level}
                  label={`${readiness.level} ${readiness.score}/100`}
                  dotPosition="trailing"
                />
              </div>
              <div className="grid gap-3 rounded-card border border-border bg-surface-muted p-4 text-sm leading-6 text-text-muted shadow-xs">
                <p>
                  <strong className="text-text">Title:</strong> {form.title || "Missing"}
                </p>
                <p>
                  <strong className="text-text">Summary:</strong> {form.summary || "Missing"}
                </p>
                <p>
                  <strong className="text-text">Location:</strong>{" "}
                  {[form.neighborhood, form.city, form.state].filter(Boolean).join(", ") ||
                    "Missing"}
                </p>
                <p>
                  <strong className="text-text">Window:</strong> {form.desiredWindow || "Missing"}
                </p>
                <p>
                  <strong className="text-text">Contact:</strong> {form.primaryContact || "Missing"}
                </p>
              </div>
              <div className="grid gap-3 rounded-card border border-border bg-surface-raised p-4">
                <Checkbox
                  checked={publishToCloud}
                  label="Post this job to the ClearScope public board after saving it locally."
                  onChange={(event) => setPublishToCloud(event.currentTarget.checked)}
                />
                <p className="text-sm leading-6 text-text-muted">
                  Public jobs hide contact details by default. Pros see the job, location, budget,
                  detail score, and safety notes.
                </p>
                <TurnstileField enabled={publishToCloud} onToken={setTurnstileToken} />
              </div>
              <div className="flex flex-wrap gap-3">
                <Button type="button" variant="secondary" onClick={() => setCurrentStep(3)}>
                  Back
                </Button>
                <Button type="button" loading={isSubmitting} onClick={() => void submit()}>
                  {publishToCloud ? "Post job" : "Save for later"}
                </Button>
              </div>
            </Card>
          ) : null}
        </section>

        <aside className="space-y-5 lg:col-start-2 xl:col-start-auto">
          <JobBriefPreviewPanel
            form={form}
            roughNotes={roughNotes}
            scopeItems={scopeItems}
            verifiedCount={verifiedCount}
          />

          <Card padding="lg" variant="surface">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-text">Detail score</h2>
                <p className="mt-2 text-sm leading-6 text-text-muted">{readiness.guidance}</p>
              </div>
              <ReadinessDial
                caption={`${verifiedCount} of 5 checks`}
                level={readiness.level}
                score={readiness.score}
                size="lg"
              />
            </div>
            <div className="mt-5 flex items-center justify-between gap-3">
              <StatusBadge
                dotPosition="trailing"
                level={readiness.level}
                label={`${readiness.level} ${readiness.score}/100`}
              />
              <span className="text-sm font-semibold text-text-muted">
                {verifiedCount}/5 checks
              </span>
            </div>
            <div className="mt-4">
              <ProgressMeter
                label="Detail score"
                tone={
                  readiness.level === "GREEN"
                    ? "green"
                    : readiness.level === "AMBER"
                      ? "amber"
                      : "red"
                }
                value={readiness.score}
              />
            </div>
          </Card>

          <Card padding="lg" variant="surface">
            <h2 className="text-xl font-semibold text-text">Things to watch</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {riskFlags.length ? (
                riskFlags.map((flag) => (
                  <Chip
                    key={flag.type}
                    tone={
                      flag.severity === "RED"
                        ? "danger"
                        : flag.severity === "AMBER"
                          ? "warning"
                          : "success"
                    }
                  >
                    {flag.label}
                  </Chip>
                ))
              ) : (
                <Chip tone="success">No warnings found</Chip>
              )}
            </div>
          </Card>
        </aside>
      </div>
    </div>
  );
}

function JobBriefPreviewPanel({
  form,
  scopeItems,
  roughNotes,
  verifiedCount,
}: {
  form: JobForm;
  scopeItems: JobScopeItem[];
  roughNotes: string;
  verifiedCount: number;
}) {
  const location = [form.neighborhood, form.city, form.state].filter(Boolean).join(", ");
  const briefTitle = form.title.trim() || "Untitled job";
  const briefSummary =
    form.summary.trim() || roughNotes.trim() || "Preview updates as the job takes shape.";

  return (
    <Card className="scope-preview" padding="lg" variant="surface">
      <div className="relative z-10">
        <p className="page-eyebrow">Live brief</p>
        <h2 className="mt-2 font-sans text-2xl font-semibold leading-tight text-text-strong">
          {briefTitle}
        </h2>
        <p className="mt-3 line-clamp-3 text-sm leading-6 text-text-muted">{briefSummary}</p>
        <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
          <div className="border-t border-border pt-3">
            <p className="cell-label">Details</p>
            <p className="mt-1 font-semibold text-text tabular">{verifiedCount}/5 confirmed</p>
          </div>
          <div className="border-t border-border pt-3">
            <p className="cell-label">Method</p>
            <p className="mt-1 font-semibold text-text">{form.preferredMethod}</p>
          </div>
        </div>
        <div className="mt-4 rounded-card border border-border bg-surface-raised/88 p-3 shadow-xs">
          <div className="flex flex-wrap gap-2">
            {scopeItems.map((item) => (
              <Chip key={item.category} tone={item.isVerified ? "success" : "warning"}>
                {item.category}
              </Chip>
            ))}
          </div>
          <p className="mt-3 text-xs font-semibold text-text-subtle">
            {location || "Location pending"}
          </p>
        </div>
      </div>
    </Card>
  );
}
